import { Component, OnInit, Input, ChangeDetectorRef, ElementRef, afterNextRender, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MenuDTO } from 'src/app/shared/model/dto/MenuDTO';
import { ensureSwiperRegistered } from 'src/app/shared/utils/lazy-swiper';
import { environment } from '../../../environments/environment';
import { DomSanitizer, SafeUrl, SafeStyle } from '@angular/platform-browser';
import { ModalController, AlertController, LoadingController } from 'src/app/core/overlay';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { User } from 'src/app/shared/model/User';
import { UtilsService } from 'src/app/core/services/utils.service';
import { NavService } from 'src/app/core/services/nav.service';
import { DebugModeService } from 'src/app/core/services/debugMode.service';
import { KeenTrackingService } from 'src/app/core/services/keenTracking.service';
import { ModalPage } from '../modal/modal.page';
import { carregarTelaDeLogin } from 'src/app/shared/utils/lazy-login';
import { SocialAuth } from '../../core/services/SocialAuth.service'
import { LogoutDialogPage } from '../shared/logout-dialog/logout-dialog.page';
import { LocalStorageService } from "src/app/core/services/localStorage.service";
import { TableService } from 'src/app/core';
import {
	caminhoDaCapa, caminhoDoDestaque, caminhoDoLogo, srcsetDaVersao, urlDaVersao, urlOriginal,
} from 'src/app/shared/utils/versao-de-imagem';

declare let FB: any

@Component({
	selector: 'app-header',
	templateUrl: './header.page.html',
	styleUrls: ['./header.page.scss'],
})

export class HeaderPage implements OnInit {

	@Input() restaurant: MenuDTO;
	user: User
	isLoggedIn: boolean
	loginMode;
	debugMode: number;
	hasMisingPayment: boolean

	isNewHomePedirDelivery = false;
	
	get isRestaurantBlocked(): boolean {
		return !!this.restaurant?.info?.blocked;
	}

	hasImages: boolean;
	hasNoImages: boolean;

	imgURL = environment.imgURL;
	imgCover: SafeUrl | null = null;
	/** Para o layout legado: background-image da capa (SafeStyle). */
	imgCoverStyle: SafeStyle | null = null;
	/** `srcset` 640/1280 da capa (null quando o ambiente não usa versões) e o JPEG para o recuo. */
	imgCoverSrcset: string | null = null;
	imgCoverOriginal: string | null = null;
	imgProfile;
	imgProfileOriginal: string | null = null;
	highlightImages;

	public currentTable;

	/**
	 * `false` no servidor e no primeiro render do cliente; `true` logo depois da hidratação.
	 *
	 * O que depende do RELÓGIO (aberto/fechado, horário de hoje) ou do APARELHO (login, mesa,
	 * pagamento pendente no localStorage) não pode sair no HTML pré-renderizado: ele é o mesmo
	 * para todo cliente e pode ter horas de idade. E o primeiro render do cliente tem de repetir
	 * o do servidor, senão a hidratação diverge. Então esses trechos só aparecem depois dela —
	 * sem SSR, no primeiro frame depois do boot, o que ninguém percebe.
	 */
	posHidratacao = false;
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly el = inject(ElementRef);

	/**
	 * Carrossel de destaques do layout legado: `false` enquanto a 1ª foto — a que aparece e vira o
	 * LCP — ainda está chegando. As demais ficam `loading=lazy` (fora da tela, o navegador não as
	 * pede) e só são liberadas depois dela, para não disputarem a banda. Medido em 18/09/2026 na
	 * okpizzaria: 4 destaques de ~58 KB saindo juntos com capa, logo e JS numa 4G lenta, e a foto
	 * visível chegando 7,6 s depois do HTML.
	 */
	destaquesLiberados = false;

	constructor(private sanitizer: DomSanitizer,
				private navService: NavService,
				private router: Router,
				private authService: AuthenticationService,
				private debugModeService: DebugModeService,
				private alertController: AlertController,
				private loadingController: LoadingController,
				private utils: UtilsService,
				private modalController: ModalController,
				private keen: KeenTrackingService,
				private socialAuthService: SocialAuth,
				private localStorageService: LocalStorageService,
				private tableService: TableService,
	) {
		afterNextRender(() => {
			this.posHidratacao = true;
			// Com SSR a 1ª foto pode ter chegado ANTES da hidratação — o `(load)` do template não
			// ouve o que já aconteceu. Se ela já está completa, libera agora.
			const primeira: HTMLImageElement | null = this.el.nativeElement.querySelector('.header-legacy .highlight-image');
			if (primeira && primeira.complete) this.destaquesLiberados = true;
			this.cdr.markForCheck();
		});
	}


	ngOnInit() {
		this.isNewHomePedirDelivery = !!this.restaurant?.info?.modules?.new_home_pedir_delivery;
		this.hasImages = !!(this.restaurant.info.profile_pic || this.restaurant.info.cover_pic);
		this.hasNoImages = !this.hasImages;
		// Versões no tamanho em que aparecem (ver versao-de-imagem.ts): o logo tem 44–50 px na tela e
		// chegava com até 3977 px. O `*Original` é o JPEG de sempre, para o recuo do index.html.
		const capa = this.restaurant.info.cover_pic ? caminhoDaCapa(this.restaurant.info.cover_pic) : null;
		const logo = this.restaurant.info.profile_pic ? caminhoDoLogo(this.restaurant.info.profile_pic) : null;
		this.imgCover = capa ? this.sanitizer.bypassSecurityTrustUrl(urlDaVersao(capa, 1280)) : null;
		this.imgCoverSrcset = capa ? srcsetDaVersao(capa, [640, 1280]) : null;
		this.imgCoverOriginal = capa ? urlOriginal(capa) : null;
		// Layout legado: a capa é fundo de CSS, que não tem recuo quando a versão falha. Fica com a
		// versão mesmo assim: é o header de ~90% das lojas, e a versão só falha se o gerador cair
		// ANTES de ela existir (depois disso sai do bucket ou do cache).
		this.imgCoverStyle = capa ? this.sanitizer.bypassSecurityTrustStyle(`url(${urlDaVersao(capa, 1280)})`) : null;
		this.imgProfile = logo ? this.sanitizer.bypassSecurityTrustUrl(urlDaVersao(logo, 240)) : null;
		this.imgProfileOriginal = logo ? urlOriginal(logo) : null;
		this.highlightImages = this.restaurant.info.highlightImages;
		// Só carrega o Swiper se HOUVER imagens (highlightImages costuma vir []
		// — e if([]) é truthy em JS, o que baixava o chunk de 183KB à toa).
		if (this.highlightImages?.length) { ensureSwiperRegistered(); }
		this.isLoggedIn = this.authService.isLoggedIn();
		this.loginMode = this.authService.getLoginMode();
		this.authService.changeStatus.subscribe(response => {
			this.isLoggedIn = response;
		});
		this.authService.changeLoginMode.subscribe(response => {
			this.loginMode = response;
		});

		this.debugMode = this.debugModeService.getMode();
		this.hasMisingPayment = this.checkMissingMercadopagoPayments();

        this.currentTable = this.tableService.currentTable;
	}

	async goToLogin() {
		// A tela de login vem por chunk, no toque (ver lazy-login.ts). Rede ruim devolve null:
		// aí seguimos pela rota, que é o mesmo destino com a recuperação do router por trás.
		const LoginPage = await carregarTelaDeLogin();
		if (!LoginPage) return this.navService.push('login');

		const loginModal = await this.modalController.create({
			component: ModalPage,
			componentProps: {
				"root": LoginPage
			}
		})

		loginModal.present();
	}

	async confirmLogout() {
		if (this.isNewHomePedirDelivery) {
			const modal = await this.modalController.create({
				component: LogoutDialogPage,
				cssClass: 'modal-logout-dialog',
				backdropDismiss: true,
			});
			modal.onDidDismiss().then(({ data }) => {
				if (data?.confirmed) this.loadingLogout(this.loginMode);
			});
			return modal.present();
		}

		const alert = await this.alertController.create({
			message: 'Tem certeza que deseja sair?',
			buttons: [
				{
					text: 'NÃO',
				},
				{
					text: 'SIM',
					handler: () => {
						this.loadingLogout(this.loginMode);
					}
				}
			]
		});
		await alert.present();
	}

	async loadingLogout(mode: string) {
		const loading = await this.loadingController.create({
			message: 'Saindo...'
		});
		await loading.present();

		if (mode == 'google'){
			await this.socialAuthService.logoutWithGoogle();
		}
		
		if (this.authService.logout()) {
			this.navService.push(`/`);
			await loading.dismiss();
		}

		this.keen.setEventBase().finally(() => {
			this.keen.event('logout');
		})
	}

	timeConvert(minutes) {
		return this.utils.timeConvert(minutes);
	}

	openReviews() {
		this.navService.push('/reviews');
	}

	openPayments() {
		this.navService.push('/payments', { restaurant: this.restaurant.info });
	}

	openHours() {
		const params: any = { hours: this.restaurant.hours };

		if (this.isNewHomePedirDelivery) {
			params.restaurant = this.restaurant.info;
		}

		this.navService.push('/hours', params);
	}

	openInfo() {
		if (this.isNewHomePedirDelivery) {
			return this.navService.push('/info-tabs', { restaurant: this.restaurant.info, hours: this.restaurant.hours });
		}
		this.navService.push('/info', { restaurant: this.restaurant.info });
	}

	checkMissingMercadopagoPayments() {
		let keys = Object.keys(this.localStorageService),
        index = 0, key;

		for (; key = keys[index]; index++) {
			if (key.indexOf('external_reference_') === 0) {
				return true
			}
		}
	}

	/** Destaque do carrossel: a `lg_` tem 600 px, então 640 só troca o formato (−34% em WebP). */
	urlDoDestaque(imagem: string): string {
		return urlDaVersao(caminhoDoDestaque(imagem), 640);
	}

	originalDoDestaque(imagem: string): string {
		return urlOriginal(caminhoDoDestaque(imagem));
	}

	/** A 1ª foto do carrossel legado chegou (ou falhou): as outras podem carregar. */
	liberarDestaques(): void {
		if (this.destaquesLiberados) return;
		this.destaquesLiberados = true;
		this.cdr.markForCheck();
	}

	getTodayHoursDisplay(): string {
		if (!this.restaurant?.hours?.hours) return '';
		const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
		const today = days[new Date().getDay()];
		const slots: any[] = this.restaurant.hours.hours[today];
		if (!slots?.length) return '';

		const fmt = (t: string) => (t && t.length >= 5) ? t.substring(0, 5) : t;
		const toMinutes = (t: string) => {
			const [h, m] = t.split(':').map(Number);
			return h * 60 + m;
		};

		const now = new Date();
		const nowMinutes = now.getHours() * 60 + now.getMinutes();

		const openSlots = slots.filter((s: any) => !s.isClosed && s.firstTime && s.secondTime);
		if (!openSlots.length) return '';

		const current = openSlots.find((s: any) => {
			const start = toMinutes(s.firstTime);
			const end = toMinutes(s.secondTime);
			return nowMinutes >= start && nowMinutes <= end;
		});

		if (current) {
			return `${fmt(current.firstTime)} - ${fmt(current.secondTime)}`;
		}

		const next = openSlots
			.filter((s: any) => toMinutes(s.firstTime) > nowMinutes)
			.sort((a: any, b: any) => toMinutes(a.firstTime) - toMinutes(b.firstTime))[0];

		if (next) {
			return `${fmt(next.firstTime)} - ${fmt(next.secondTime)}`;
		}

		return '';
	}

}
